/**
 * Central explainability registry — `Source & calcul` for every KPI in Noam.
 *
 * Each key is referenced by the `<SourceCalcBtn kpiKey="..." />` component.
 * Add new entries here when wiring a new metric.
 */
export const KPI_REGISTRY = {
  // ------------ Executive ------------
  cout_inaction: {
    title: "Coût de l'inaction · risques critiques",
    sources: ["workspace_risks (impact_net × probabilite_nette ≥ 12)", "Champ cout_inaction de chaque risque"],
    formula: "Σ (cout_inaction) pour les risques nets ≥ 12, plus Σ global de tous les coûts d'inaction.",
    refresh: "Recalculé à chaque chargement de la vue Executive depuis MongoDB.",
    links: [{ label: "Ouvrir les risques critiques", to: "/app/risques?level=critical" }],
  },
  financabilite: {
    title: "Score de finançabilité",
    sources: ["workspace_positionnement.profile + workspace_fin_actifs (LTV pondéré)"],
    formula: "Score composite (0–100) = qualité collatéral + LTV moyen + diversification d'actifs.",
    refresh: "Recalculé live à l'ouverture de la page.",
    links: [{ label: "Ouvrir Financement", to: "/app/financement" }],
  },
  ltv: {
    title: "LTV moyen portefeuille",
    sources: ["workspace_fin_actifs (valeur × LTV par classe)"],
    formula: "LTV = Σ (valeur × LTV) / Σ valeur — pondéré par valeur d'actif.",
    refresh: "Seuil d'alerte à 75%. Mise à jour à chaque écriture d'actif.",
    links: [{ label: "Voir les actifs", to: "/app/financement" }],
  },
  conformite: {
    title: "Taux de conformité",
    sources: ["workspace_audits (statut 'clôturé' avec 0 NC ouverte)", "workspace_controles (statut 'efficace')"],
    formula: "(audits clôturés sans NC + contrôles efficaces) / total × 100.",
    refresh: "Mise à jour à chaque changement de statut d'audit ou de contrôle.",
    links: [{ label: "Voir les audits", to: "/app/audits" }, { label: "Voir les contrôles", to: "/app/controles" }],
  },

  // ------------ Score / Note de pilotage ------------
  score_global: {
    title: "Note de pilotage globale",
    sources: ["workspace_risks (net = impact × probabilité)", "workspace_audits (taux clôture)", "workspace_hr_postes (couverture succession)", "workspace_fin_actifs (LTV)"],
    formula: "Moyenne pondérée des 5 piliers (Risques 35% · Conformité 20% · RH 15% · Finance 15% · Opérations 15%) → grade A→F.",
    refresh: "Recalculée à chaque mutation d'objet de l'environnement.",
    links: [{ label: "Détail des piliers", to: "/app/score" }],
  },
  pilier_risques: {
    title: "Pilier Risques",
    sources: ["workspace_risks (top 10 nets ≥ 6)"],
    formula: "100 − Σ(net × 2) avec plafond. Plus le score net est élevé, plus le pilier baisse.",
    refresh: "Live.",
    links: [{ label: "Voir les risques", to: "/app/risques" }],
  },
  pilier_conformite: {
    title: "Pilier Conformité",
    sources: ["workspace_audits + workspace_controles + référentiels ISO/DORA/RGPD-LPD/FINMA"],
    formula: "Couverture pondérée des référentiels actifs × taux de contrôles efficaces.",
    refresh: "Live.",
    links: [{ label: "Voir les audits", to: "/app/audits" }],
  },
  pilier_rh: {
    title: "Pilier RH",
    sources: ["workspace_hr_postes (couverture succession)"],
    formula: "90 − (postes critiques sans succession × 8), borné 0–100.",
    refresh: "Live.",
    links: [{ label: "Voir les postes RH", to: "/app/rh" }],
  },
  pilier_finance: {
    title: "Pilier Finance",
    sources: ["workspace_fin_actifs (LTV) + workspace_positionnement (finançabilité)"],
    formula: "Moyenne (score finançabilité, 100 − LTV moyen × 100, diversification).",
    refresh: "Live.",
    links: [{ label: "Voir Financement", to: "/app/financement" }],
  },
  pilier_operations: {
    title: "Pilier Opérations",
    sources: ["workspace_controles + workspace_incidents + workspace_alertes"],
    formula: "Taux de contrôles à jour × (1 − incidents critiques ouverts / total).",
    refresh: "Live.",
    links: [{ label: "Voir les contrôles", to: "/app/controles" }],
  },

  // ------------ RH ------------
  rh_score: {
    title: "Score RH consolidé",
    sources: ["workspace_hr_postes (champ succession)"],
    formula: "max(0, min(100, 90 − 8 × nombre de postes critiques sans succession)).",
    refresh: "Live.",
    links: [{ label: "Cartographier les postes", to: "/app/rh" }],
  },
  rh_postes_critiques: {
    title: "Postes critiques sans succession",
    sources: ["workspace_hr_postes où succession = 'absente'"],
    formula: "Comptage simple sur les postes critiques.",
    refresh: "Live.",
    links: [{ label: "Voir les postes RH", to: "/app/rh" }],
  },

  // ------------ Financement ------------
  fin_score: {
    title: "Score de finançabilité (Financement)",
    sources: ["workspace_fin_actifs (valeur, LTV, classe) + workspace_positionnement.profile"],
    formula: "Combinaison LTV moyen pondéré + qualité collatéral + diversification.",
    refresh: "Live.",
    links: [{ label: "Voir les actifs", to: "/app/financement" }],
  },
  fin_ltv: {
    title: "LTV global du portefeuille",
    sources: ["workspace_fin_actifs"],
    formula: "Σ(valeur × LTV) / Σ valeur, exprimé en pourcentage.",
    refresh: "Live. Seuil d'alerte 75%.",
    links: [{ label: "Voir les actifs", to: "/app/financement" }],
  },

  // ------------ Dashboard / KPIs synthèse ------------
  dash_risques_critiques: {
    title: "Risques critiques",
    sources: ["workspace_risks où impact_net × probabilite_nette ≥ 12"],
    formula: "Comptage des risques nets ≥ 12. Le delta compare à la période précédente.",
    refresh: "Live à chaque ouverture du dashboard.",
    links: [{ label: "Voir les risques critiques", to: "/app/risques?level=critical" }],
  },
  dash_controles: {
    title: "Contrôles en retard",
    sources: ["workspace_controles où statut = 'inefficace' OU date_prochaine < today"],
    formula: "Comptage des contrôles dont la date prévue est dépassée ou marqués inefficaces.",
    refresh: "Live.",
    links: [{ label: "Voir les contrôles", to: "/app/controles" }],
  },
  dash_conformite: {
    title: "Taux de conformité (dashboard)",
    sources: ["workspace_audits (clôturés sans NC) + workspace_controles (efficaces)"],
    formula: "Identique au KPI Executive : (audits clôturés sans NC + contrôles efficaces) / total × 100.",
    refresh: "Live.",
    links: [{ label: "Voir les audits", to: "/app/audits" }],
  },
  dash_plans: {
    title: "Plans d'action actifs",
    sources: ["workspace_plans où statut ∈ {planifie, en_cours}"],
    formula: "Comptage des plans dont la progression < 100% et non clôturés.",
    refresh: "Live.",
    links: [{ label: "Voir les plans", to: "/app/plans" }],
  },

  // ------------ Audits ------------
  audits_nc_ouvertes: {
    title: "Non-conformités ouvertes",
    sources: ["workspace_audits.nb_nc + workspace_audits.statut"],
    formula: "Σ des nb_nc sur audits dont statut ≠ 'cloture'.",
    refresh: "Live.",
    links: [{ label: "Voir les audits ouverts", to: "/app/audits" }],
  },

  // ------------ Fournisseurs ------------
  fournisseurs_score: {
    title: "Score risque fournisseur",
    sources: ["workspace_fournisseurs.score_risque (saisie manuelle ou IA)"],
    formula: "Moyenne arithmétique des scores. Polarité positive : >70 = sain, 50-70 = surveillance, <50 = critique.",
    refresh: "Live.",
    links: [{ label: "Voir les fournisseurs", to: "/app/fournisseurs" }],
  },
};
